import { useCallback, useMemo } from "react";

import qs from "qs";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, Params, Outlet } from "react-router-dom";

import { styled } from "@mui/system";
import Typography from "@mui/material/Typography";

import { motion } from "framer-motion";

import Code from "../../assets/icons/Code.svg";
import Manual from "../../assets/icons/Manual.svg";
// import Nfc from "../../assets/icons/Nfc.svg";

import Title from "../../components/Title";
import BackButton from "../../components/BackButton";
import SvgIcon from "../../components/SvgIcon";

import OfflineModal from "../../components/Offline/OfflineModal";
import Curve from "../../components/Animations/PageTransitions/Curve";
import Bump from "../../components/Animations/Bump";
import {
  useCheckFlowExtraButtons,
  useClientPlan,
  useCommunicationProvider,
  useDigitalReceptionMetadata,
  useHidScannerSettings,
  useSocketScannerSettings,
} from "../../lib/hooks/useQueryBoard";
import { useTeamsCallerTokenAndTargetTeamsUserIds } from "../../lib/hooks/useTeamsCallerTokenAndTargetTeamsUserIds";
import { useOfflineContext } from "../../context/Offline";
import { useLanguage } from "../../bit/language";

const MUIOptionButtonGroup = styled("div", {
  shouldForwardProp: (prop) => prop !== "count",
})<{ count?: number }>(({ count = 2 }) => ({
  maxWidth: "90.4rem",
  width: "96%",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  padding: "4.8rem 0 2.4rem",

  "@media screen and (orientation: landscape)": {
    maxWidth: "180.8rem",
    flexDirection: "row",
    padding: "0",

    "> div": {
      width: `${100 / Math.max(count, 2)}%`,
    },
  },
}));

export const MUIOptionButton = styled(motion.button)(({ theme }) => ({
  position: "relative",
  width: "90%",
  height: "16rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  margin: "2.25rem 0 3.5rem",
  borderRadius: "1rem",
  border: `0.2rem solid ${theme.palette.primary.main}`,
  backgroundColor: theme.palette.primary.main,

  ":hover": {
    backgroundColor: theme.palette.primary.dark,
    border: `0.2rem solid ${theme.palette.primary.dark}`,
  },

  svg: {
    position: "absolute",
    left: "4rem",
    fill: theme.palette.primary.contrastText,
  },

  h3: {
    color: theme.palette.primary.contrastText,
    margin: "0 12rem",
  },

  "@media screen and (orientation: landscape)": {
    height: "14rem",
    margin: "0 1rem 6.4rem 6rem",
  },
}));

const LOGO_LEFT = "4rem";
const DEFAULT_LOGO_WIDTH = "8rem";

export const MUIOptionLogoButton = styled(MUIOptionButton, {
  shouldForwardProp: (prop) =>
    prop !== "overlayColor" &&
    prop !== "frameColor" &&
    prop !== "logoBackgroundColor" &&
    prop !== "logoWidth",
})<{
  overlayColor?: string;
  frameColor?: string;
  logoBackgroundColor?: string;
  logoWidth?: string;
}>(
  ({
    overlayColor,
    frameColor,
    logoBackgroundColor,
    logoWidth = DEFAULT_LOGO_WIDTH,
  }) => {
    const colors = {
      ...(logoBackgroundColor ? { backgroundColor: logoBackgroundColor } : {}),
      ...(frameColor ? { borderColor: frameColor } : {}),
    };

    return {
      overflow: "hidden",

      ...colors,
      ":hover": colors,
      ":active": colors,

      ...(overlayColor
        ? {
            "::after": {
              content: '""',
              position: "absolute",
              inset: 0,
              backgroundColor: overlayColor,
            },
          }
        : {}),

      svg: {
        zIndex: 1,
        maxHeight: "10rem",
      },

      h3: {
        position: "relative",
        zIndex: 1,
        margin: `0 calc(${LOGO_LEFT} + ${logoWidth})`,
      },

      ":disabled": {
        opacity: 0.5,
      },
    };
  },
);

interface IQueryParams extends Params {
  action: "checkin" | "checkout";
}

const CheckOptions = () => {
  const navigate = useNavigate();

  const { action } = useParams() as IQueryParams;

  const { t } = useTranslation();

  const metadata = useDigitalReceptionMetadata();

  const clientPlan = useClientPlan();
  const { isHidScannerActive } = useHidScannerSettings();
  const { isSocketScannerActive } = useSocketScannerSettings();

  const hasCameraScanner =
    clientPlan !== "free" &&
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices;

  const hasAnyQrScanner =
    isHidScannerActive || isSocketScannerActive || hasCameraScanner;

  const [language] = useLanguage();

  const title = metadata?.uiOverrides?.home?.flows?.[action]?.title?.[language];

  const codeText =
    metadata?.uiOverrides?.home?.flows?.[action]?.buttons?.code?.[language];

  const manualText =
    metadata?.uiOverrides?.home?.flows?.[action]?.buttons?.manual?.[language];

  const { online } = useOfflineContext();

  const extraButtons = useCheckFlowExtraButtons(action);

  const communicationProvider = useCommunicationProvider();

  const isTeamsExtraButton = useCallback(
    (button: ICheckFlowExtraButton) =>
      communicationProvider === "teams" && !button.phone?.trim(),
    [communicationProvider],
  );

  const extraButtonEmails = useMemo(
    () =>
      extraButtons
        .filter(isTeamsExtraButton)
        .map((button) => button.email)
        .filter((email): email is string => !!email?.trim()),
    [extraButtons, isTeamsExtraButton],
  );

  const { callerAcsToken, data: teamsData } =
    useTeamsCallerTokenAndTargetTeamsUserIds({
      emails: extraButtonEmails,
    });

  const visibleExtraButtons = useMemo(
    () =>
      extraButtons.filter((button) =>
        isTeamsExtraButton(button)
          ? !!button.email?.trim()
          : !!button.phone?.trim(),
      ),
    [extraButtons, isTeamsExtraButton],
  );

  const getTargetTeamsUser = useCallback(
    (email?: string) =>
      teamsData?.getCallerAcsTokenAndTargetTeamsUserIds?.targetTeamsUsers?.find(
        (targetTeamsUser) => targetTeamsUser.email === email,
      ),
    [teamsData],
  );

  const isExtraButtonDisabled = useCallback(
    (button: ICheckFlowExtraButton) => {
      if (!online) {
        return true;
      }

      if (isTeamsExtraButton(button)) {
        const targetTeamsUser = getTargetTeamsUser(button.email);

        if (!targetTeamsUser) {
          return true;
        }

        return (
          !targetTeamsUser.isGroup &&
          !targetTeamsUser.teamsUsers?.[0]?.teamsUserId
        );
      }

      return !button.phone?.trim();
    },
    [online, isTeamsExtraButton, getTargetTeamsUser],
  );

  const handleClickExtraButton = useCallback(
    (button: ICheckFlowExtraButton) => {
      if (isTeamsExtraButton(button)) {
        const targetTeamsUser = getTargetTeamsUser(button.email);

        const targetTeamsUserIds = targetTeamsUser?.teamsUsers
          ?.map((user) => user.teamsUserId)
          .filter(Boolean)
          .join(",");

        return navigate(
          `/${
            button.teamsCall === "video"
              ? "teams-video"
              : `access/option/${action}/teams-audio`
          }-${targetTeamsUser?.isGroup ? "group-" : ""}call?${qs.stringify({
            callerAcsToken,
            targetTeamsUserIds,
          })}`,
          { replace: true },
        );
      }

      return navigate(
        `/access/option/${action}/contact/extra-${button.id}/call`,
        { replace: true },
      );
    },
    [action, navigate, isTeamsExtraButton, getTargetTeamsUser, callerAcsToken],
  );

  return (
    <Curve>
      <div className="d-flex align-items-center flex-column">
        <BackButton to="/" />
        <Title className="page-title home">
          <Typography variant="h1">
            {title || t(`check:option.${action}.title`)}
          </Typography>
        </Title>
        <MUIOptionButtonGroup count={2 + visibleExtraButtons.length}>
          <Bump>
            <MUIOptionButton
              onClick={() => {
                return navigate(`/access/${action}`, { replace: true });
              }}
            >
              <Code width="8rem" />
              <Typography variant="h3">
                {codeText ||
                  t(
                    `check:option.${action}.code${
                      hasAnyQrScanner ? "" : "-no-qr"
                    }`,
                  )}
              </Typography>
            </MUIOptionButton>
          </Bump>
          <Bump>
            <MUIOptionButton
              onClick={() => {
                return navigate(`/access/${action}/manual`, { replace: true });
              }}
            >
              <Manual width="8rem" />
              <Typography variant="h3">
                {manualText || t(`check:option.${action}.manual`)}
              </Typography>
            </MUIOptionButton>
          </Bump>
          {visibleExtraButtons.map((button) => (
            <Bump key={button.id}>
              <MUIOptionLogoButton
                disabled={isExtraButtonDisabled(button)}
                overlayColor={button.overlayColor}
                frameColor={button.borderColor || button.overlayColor}
                logoBackgroundColor={button.backgroundColor}
                logoWidth={button.icon?.width}
                onClick={() => handleClickExtraButton(button)}
              >
                {!!button.icon?.svg && (
                  <SvgIcon
                    id={button.icon.svg}
                    width={button.icon.width || DEFAULT_LOGO_WIDTH}
                  />
                )}
                {!!(button.label?.[language] || button.label?.en) && (
                  <Typography variant="h3">
                    {button.label?.[language] || button.label?.en}
                  </Typography>
                )}
              </MUIOptionLogoButton>
            </Bump>
          ))}
        </MUIOptionButtonGroup>
      </div>
      <OfflineModal />
      <Outlet />
    </Curve>
  );
};

export default CheckOptions;
